import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Zap, Sparkles, Receipt, CreditCard, Coins, Repeat, Clock } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button, Card, Badge, Spinner } from "@/components/kit";

const rupiah = (n) => "Rp" + (n || 0).toLocaleString("id-ID");

function loadSnap(clientKey, isSandbox) {
  return new Promise((resolve) => {
    if (window.snap) return resolve();
    const s = document.createElement("script");
    s.src = isSandbox ? "https://app.sandbox.midtrans.com/snap/snap.js" : "https://app.midtrans.com/snap/snap.js";
    s.setAttribute("data-client-key", clientKey);
    s.onload = () => resolve();
    document.body.appendChild(s);
  });
}

export default function Billing() {
  const { user, quota, loadUser, refreshQuota } = useAuth();
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [creditCost, setCreditCost] = useState({});
  const [overage, setOverage] = useState(150);
  const [invoices, setInvoices] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState("");
  const [autoTopup, setAutoTopup] = useState(user?.auto_topup || false);

  const loadAll = () => {
    api.get("/quota/plans").then((r) => {
      setPlans(r.data.plans); setPacks(r.data.credit_packs);
      setCreditCost(r.data.credit_cost); setOverage(r.data.overage_rate_idr);
    });
    api.get("/payment/invoices").then((r) => setInvoices(r.data.invoices));
  };

  useEffect(() => {
    loadAll();
    api.get("/payment/config").then((r) => { setConfig(r.data); loadSnap(r.data.client_key, r.data.is_sandbox); });
    const p = new URLSearchParams(window.location.search);
    if (p.get("ipaymu") === "return") {
      toast.info("Memeriksa status pembayaran iPaymu...");
      const oid = p.get("order_id");
      if (oid) { api.post(`/payment/simulate/${oid}`).then(() => { loadUser(); refreshQuota(); loadAll(); toast.success("Pembayaran diproses!"); }).catch(() => {}); }
      window.history.replaceState({}, "", "/app/billing");
    }
  }, []);
  useEffect(() => { setAutoTopup(user?.auto_topup || false); }, [user]);

  const afterPay = async (orderId) => {
    try { await api.post(`/payment/simulate/${orderId}`); } catch {}
    await loadUser(); await refreshQuota(); loadAll();
    toast.success("Pembayaran berhasil!");
  };

  const pay = async (endpoint, payload, id) => {
    if (busy) return;
    setBusy(id);
    try {
      const { data } = await api.post(endpoint, payload);
      if (data.gateway === "ipaymu") {
        if (data.redirect_url) { window.location.href = data.redirect_url; return; }
        toast.error("Gagal membuat pembayaran iPaymu");
        setBusy("");
        return;
      }
      if (window.snap && data.snap_token) {
        window.snap.pay(data.snap_token, {
          onSuccess: () => afterPay(data.order_id),
          onPending: () => afterPay(data.order_id),
          onError: () => toast.error("Pembayaran gagal"),
          onClose: () => setBusy(""),
        });
      } else if (data.redirect_url) { window.open(data.redirect_url, "_blank"); }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const toggleAuto = async () => {
    const next = !autoTopup;
    setAutoTopup(next);
    try { await api.post("/payment/auto-topup", { enabled: next }); await loadUser(); toast.success(next ? "Auto top-up aktif" : "Auto top-up nonaktif"); }
    catch (e) { setAutoTopup(!next); toast.error(apiErr(e)); }
  };

  if (plans.length === 0) return <div className="flex justify-center py-20"><Spinner className="w-7 h-7" /></div>;
  const cs = quota?.credits;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-3"><span className="w-10 h-10 rounded-xl bg-sky-100 grid place-items-center shrink-0"><CreditCard className="w-5 h-5 text-sky-600" /></span><h1 className="font-display text-3xl font-extrabold">Billing & Kredit</h1></span>
          <p className="text-slate-500 mt-1">Plan aktif: <Badge color="sky" className="capitalize">{user?.plan}</Badge></p>
        </div>
        <div className="flex bg-slate-100 rounded-xl p-1">
          {["monthly", "yearly"].map((b) => (
            <button key={b} onClick={() => setBilling(b)} className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${billing === b ? "bg-white text-sky-600 shadow-sm" : "text-slate-500"}`}>
              {b === "monthly" ? "Bulanan" : "Tahunan -20%"}
            </button>
          ))}
        </div>
      </div>

      {/* Value anchor */}
      <Card className="p-5 bg-sky-50 border-sky-200">
        <p className="text-sm text-sky-900">💡 <b>1 kredit = 1 lead ditemukan.</b> Riset manual butuh 3–5 jam/hari. Dengan RESADA, ribuan lead dalam menit — jauh lebih murah dari 1 jam gaji staff.</p>
      </Card>

      {/* Credit status */}
      {cs && (
        <div className="grid sm:grid-cols-3 gap-4">
          <Card className="p-5">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-2"><Coins className="w-4 h-4 text-sky-500" /> Kredit bulan ini</div>
            <p className="font-display text-2xl font-extrabold">{cs.monthly_remaining}<span className="text-slate-400 text-base">/{cs.monthly_credits}</span></p>
            <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className="h-full bg-sky-500" style={{ width: `${cs.monthly_credits ? Math.min(cs.credits_used / cs.monthly_credits * 100, 100) : 0}%` }} /></div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-2"><Sparkles className="w-4 h-4 text-emerald-500" /> Kredit PAYG (rollover)</div>
            <p className="font-display text-2xl font-extrabold text-emerald-600">{cs.payg_balance}</p>
            <p className="text-xs text-slate-400 mt-1">Tidak hangus tiap bulan</p>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-slate-500 text-sm"><Repeat className="w-4 h-4 text-violet-500" /> Auto Top-up</div>
              <button onClick={toggleAuto} data-testid="auto-topup-toggle" className={`w-11 h-6 rounded-full transition relative ${autoTopup ? "bg-sky-500" : "bg-slate-200"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${autoTopup ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
            <p className="text-xs text-slate-500">Kredit habis → otomatis beli paket {packs[0]?.label} agar scraping tak terhenti.</p>
          </Card>
        </div>
      )}

      {config?.gateway === "ipaymu" ? (
        <Card className="p-3 bg-sky-50 border-sky-200 text-sky-700 text-xs">
          Pembayaran via <b>iPaymu</b>{config?.ipaymu_sandbox ? " (Sandbox)" : ""}. Anda akan diarahkan ke halaman pembayaran iPaymu.
        </Card>
      ) : config?.is_sandbox && (
        <Card className="p-3 bg-amber-50 border-amber-200 text-amber-700 text-xs">
          Mode Sandbox Midtrans. Pakai kartu test 4811 1111 1111 1114 — atau pembayaran otomatis difinalisasi untuk demo.
        </Card>
      )}

      {/* Plans */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const price = billing === "yearly" ? p.price_yearly : p.price_monthly;
          const isCurrent = user?.plan === p.id;
          const popular = p.id === "pro";
          return (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`rounded-2xl p-6 border transition ${popular ? "border-sky-400 sky-glow" : "border-slate-200"} bg-white`}>
              {popular && <Badge color="sky" className="mb-2"><Sparkles className="w-3 h-3" /> Populer</Badge>}
              <h3 className="font-display text-xl font-bold capitalize">{p.label}</h3>
              <p className="mt-2"><span className="font-display text-3xl font-extrabold">{p.id === "free" ? "Gratis" : rupiah(price)}</span>{p.id !== "free" && <span className="text-slate-400 text-sm">/{billing === "yearly" ? "thn" : "bln"}</span>}</p>
              <ul className="mt-5 space-y-2 text-sm text-slate-600">
                <li className="flex gap-2"><Coins className="w-4 h-4 text-sky-500 mt-0.5" /> <b>{p.credits.toLocaleString("id-ID")} kredit</b>/bln</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-sky-500 mt-0.5" /> Cap harian {p.daily_credit_cap.toLocaleString("id-ID")} kredit</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-sky-500 mt-0.5" /> {p.wa_devices} WA device</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-sky-500 mt-0.5" /> {p.blast_wa.toLocaleString("id-ID")} WA blast</li>
              </ul>
              <div className="mt-6">
                {isCurrent ? <Button variant="outline" className="w-full" disabled>Plan Aktif</Button> :
                  p.id === "free" ? <Button variant="ghost" className="w-full" disabled>—</Button> :
                    <Button data-testid={`subscribe-${p.id}`} className="w-full" variant={popular ? "primary" : "outline"} loading={busy === p.id}
                      onClick={() => pay("/payment/subscribe", { plan_id: p.id, billing }, p.id)}>
                      <Zap className="w-4 h-4" /> Pilih
                    </Button>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Credit packs */}
      <div>
        <h2 className="font-display text-xl font-bold mb-1">Top-up Kredit (PAYG)</h2>
        <p className="text-sm text-slate-500 mb-3">Butuh lebih? Beli kredit tambahan — rollover, tidak hangus. Overage normal ~{rupiah(overage)}/kredit, pack lebih hemat.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {packs.map((p) => (
            <Card key={p.id} className="p-5">
              <Coins className="w-6 h-6 text-sky-500 mb-2" />
              <p className="font-semibold text-slate-800">{p.label}</p>
              <p className="font-display text-2xl font-extrabold mt-1">{rupiah(p.price)}</p>
              <p className="text-xs text-slate-400">≈ {rupiah(Math.round(p.price / p.credits))}/kredit</p>
              <Button data-testid={`pack-${p.id}`} variant="soft" size="sm" className="w-full mt-4" loading={busy === p.id}
                onClick={() => pay("/payment/credit-pack", { pack_id: p.id }, p.id)}>
                <CreditCard className="w-4 h-4" /> Beli
              </Button>
            </Card>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">Konsumsi kredit: Google Maps {creditCost.google_maps || 1}/lead · Facebook {creditCost.facebook || 2}/lead · Instagram {creditCost.instagram || 2}/lead.</p>
      </div>

      {/* Invoices */}
      <div>
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><Receipt className="w-5 h-5" /> Riwayat Pembayaran</h2>
        {invoices.length === 0 ? <Card className="p-8 text-center text-slate-400 text-sm">Belum ada transaksi.</Card> : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="p-3">Order ID</th><th className="p-3">Tipe</th><th className="p-3">Jumlah</th><th className="p-3">Status</th></tr></thead>
              <tbody>
                {invoices.map((iv) => (
                  <tr key={iv.order_id} className="border-t border-slate-50">
                    <td className="p-3 font-mono text-xs">{iv.order_id}</td>
                    <td className="p-3 capitalize">{(iv.type || "").replace("_", " ")}{iv.auto ? " (auto)" : ""}</td>
                    <td className="p-3 font-semibold">{rupiah(iv.gross_amount)}</td>
                    <td className="p-3"><Badge color={iv.paid ? "green" : "amber"}>{iv.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
